import api from './client';

export interface Agrupamento {
  grupoId: number;
  descricao: string;
  qtdeProdutos?: number;
  produtoAgrupamentos?: ProdutoAgrupamento[];
}

export interface ProdutoAgrupamento {
  produtoCodigo: string;
  grupoId: number;
  nomeEmbalagem: string;
  visivelCat?: string;
  descricaoProduto?: string;
  referencia?: string;
  codigoFabrica?: string;
  embalagem?: string;
  qtdeEmbalagem?: number;
  dataInclusao?: string;
  agrupamento?: Agrupamento;
  produto?: ProdutoGeral;
}

export interface ProdutoGeral {
  codigo: string;
  descricao: string;
  referencia?: string;
  codigoFabrica?: string;
  embalagem?: string;
  qtdeEmbalagem?: number;
  marca?: string;
  grupo?: number;
  subgrupo?: number;
}

export interface ProdutoDisponivelDto {
  codigo: string;
  descricao: string;
  referencia?: string;
  codigoFabrica?: string;
  unidade?: string;
  qtdeEmbalagem?: number;
}

export interface ProdutoManutencao {
  codigo: string;
  descricao: string;
  referencia?: string;
  codigoFabrica?: string;
  embalagem?: string;
  qtdeEmbalagem?: number;
  grupoId?: number | null;
  grupoDescricao?: string | null;
  nomeEmbalagem?: string | null;
  visivelCat: string;
  dataAtualizacao?: string | null;
  horaAtualizacao?: string | null;
}

export interface AgrupamentoSugestaoDto {
  termos: string;
  qtdeSugeridos: number;
}

export interface ManutencaoLoteItem {
  codigo: string;
  grupoIdAnterior?: number | null;
  grupoIdNovo?: number | null;
  nomeEmbalagem?: string | null;
  visivelCat: string;
}

export interface CriarAgrupamentoRequest {
  descricao: string;
  produtosComEmbalagem?: Record<string, string>;
}

export interface AtualizarProdutosRequest {
  produtosComEmbalagem?: Record<string, string>;
}

class AgrupamentoService {
  async listarAgrupamentos(termoBusca?: string, forceRefresh = false): Promise<Agrupamento[]> {
    const params = new URLSearchParams();
    if (termoBusca) params.append('termoBusca', termoBusca);
    if (forceRefresh) params.append('forceRefresh', 'true');
    const response = await api.get<Agrupamento[]>(`/agrupamento?${params}`);
    return response.data;
  }

  async buscarAgrupamento(id: number): Promise<Agrupamento> {
    const response = await api.get<Agrupamento>(`/agrupamento/${id}`);
    return response.data;
  }

  async buscarProdutosDoAgrupamento(id: number, forceRefresh = false): Promise<ProdutoAgrupamento[]> {
    const params = forceRefresh ? '?forceRefresh=true' : '';
    const response = await api.get<ProdutoAgrupamento[]>(`/agrupamento/${id}/produtos${params}`);
    return response.data;
  }

  async listarProdutosDisponiveis(agrupamentoAtualId?: number): Promise<ProdutoGeral[]> {
    const params = agrupamentoAtualId ? `?agrupamentoAtualId=${agrupamentoAtualId}` : '';
    const response = await api.get<ProdutoGeral[]>(`/agrupamento/produtos-disponiveis${params}`);
    return response.data;
  }

  async buscarProdutosDisponiveisComBusca(agrupamentoAtualId?: number, termoBusca?: string, limite = 50): Promise<ProdutoGeral[]> {
    const params = new URLSearchParams();
    if (agrupamentoAtualId) params.append('agrupamentoAtualId', agrupamentoAtualId.toString());
    if (termoBusca?.trim()) params.append('termoBusca', termoBusca.trim());
    params.append('limite', limite.toString());

    const response = await api.get<ProdutoDisponivelDto[]>(`/agrupamento/produtos-disponiveis-busca?${params}`);
    return response.data.map(p => ({
      codigo: p.codigo,
      descricao: p.descricao,
      referencia: p.referencia,
      codigoFabrica: p.codigoFabrica,
      embalagem: p.unidade,
      qtdeEmbalagem: p.qtdeEmbalagem,
      marca: undefined,
      grupo: undefined,
      subgrupo: undefined,
    }));
  }

  async criarAgrupamento(request: CriarAgrupamentoRequest): Promise<Agrupamento> {
    const response = await api.post<Agrupamento>('/agrupamento', request);
    return response.data;
  }

  async atualizarProdutosAgrupamento(id: number, request: AtualizarProdutosRequest): Promise<void> {
    await api.put(`/agrupamento/${id}/produtos`, request);
  }

  async removerProdutoDoAgrupamento(id: number, produtoCodigo: string): Promise<void> {
    await api.delete(`/agrupamento/${id}/produtos/${encodeURIComponent(produtoCodigo)}`);
  }

  async listarProdutosManutencao(marca: string, termo: string, limite = 100): Promise<ProdutoManutencao[]> {
    const params = new URLSearchParams({ marca, termo, limite: String(limite) });
    const response = await api.get<ProdutoManutencao[]>(`/agrupamento/manutencao?${params}`);
    return response.data;
  }

  async salvarManutencaoLote(items: ManutencaoLoteItem[]): Promise<void> {
    await api.put('/agrupamento/manutencao', items);
  }

  async excluirAgrupamento(id: number): Promise<void> {
    await api.delete(`/agrupamento/${id}`);
  }

  async renomearAgrupamento(id: number, descricao: string): Promise<void> {
    await api.put(`/agrupamento/${id}`, { descricao });
  }

  async listarSugestoes(): Promise<Record<number, AgrupamentoSugestaoDto>> {
    const response = await api.get<Record<number, AgrupamentoSugestaoDto>>('/agrupamento/sugestoes');
    return response.data;
  }

  async recalcularSugestoes(): Promise<void> {
    await api.post('/agrupamento/sugestoes/recalcular');
  }
}

export const agrupamentoService = new AgrupamentoService();
